import { computed, defineComponent, nextTick, reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import moment from 'moment';
import { withLoading } from '@hems/component';
import { UserReportService } from '@hems/service';
import { divideArray, getEnergyBalance, getParam, getDateInitialValue, getNowByClient, } from '@hems/user/src/containers/report/chartFunctions';
import { getPvGenerationOptions } from '@hems/user/src/containers/report/generation/chartOptions';
import { useLocale } from '@hems/util';
import { getTime } from '@hems/util/src/helper/dateHelper';
import { GraphChart, EnergyBalance, CalendarHourly, CalendarDaily, CalendarMonth } from '@/components';
export default defineComponent({
    name: 'PVGenerationContainer',
    components: {
        GraphChart,
        EnergyBalance,
        CalendarHourly,
        CalendarDaily,
        CalendarMonth,
    },
    props: {
        tabname: {
            type: String,
            required: true,
            default: 'hourly',
        },
    },
    setup(props) {
        const { t } = useI18n();
        const store = useStore();
        const state = reactive({
            tabname: props.tabname,
            showChart: false,
            data: {
                generation: [],
                co2: [],
            },
            energyBalance: [],
            date: null,
        });
        const defaultBaseDeviceInfo = {
            device_id: '',
            site_id: 0,
            timezone_id: '',
            product_model_nm: '',
        };
        const { intlFormatLocale } = useLocale();
        const timeZoneId = computed(() => store.state.appCtx.timeZone ?? '');
        const baseDeviceInfo = computed(() => store.state.device.baseDeviceInfo ?? null);
        const deviceInstalledDate = computed(() => store.state.device.installation?.install_dt);
        const service = new UserReportService(window.axiosInstance.axios);
        const options = computed(() => getPvGenerationOptions(state.tabname, intlFormatLocale.value, state.data, t));
        async function getReportsByInterval(tabname) {
            if (state.date == null)
                return;
            const date = state.date;
            await withLoading(async () => {
                await service
                    .getReportsByInterval(tabname, getParam(date, 'gen', state.tabname, baseDeviceInfo.value ?? defaultBaseDeviceInfo))
                    .then(({ result }) => {
                    state.energyBalance = divideArray([
                        {
                            value: result.gen_kwh.origin || 0,
                            unit: 'kWh',
                            type: 'device.produced',
                            detailMessageInfo: {
                                isInfo: result.third_party === 'Y',
                                message: 'The real-time production power is the sum of PV module with Q.HOME CORE and the 3rd Party PV module.',
                            },
                        },
                    ], 2);
                    state.data.generation = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.gen_data[index] || 0,
                    }));
                    state.data.co2 = result.colec_time.map((TIME, index) => ({
                        x: getTime(TIME, tabname),
                        y: result.gen_co2[index] || 0,
                    }));
                })
                    .catch((e) => {
                    console.log(e);
                    state.energyBalance = [[], [], [], [], []];
                    state.data.generation = [];
                    state.data.co2 = [];
                });
            })();
            nextTick(() => (state.showChart = true));
        }
        function rerenderDataGrid() {
            state.showChart = false;
            nextTick(() => (state.showChart = true));
        }
        function getServerTime() {
            const isLifeTime = false;
            const installedDate = moment(deviceInstalledDate.value).toDate();
            service
                .getServerTime(baseDeviceInfo.value?.timezone_id ?? '')
                .then(async (res) => {
                state.date = isLifeTime ? getDateInitialValue(res, installedDate) : getDateInitialValue(res);
            })
                .catch(async (e) => {
                console.error(e);
                state.date = isLifeTime
                    ? getDateInitialValue(getNowByClient(timeZoneId.value), installedDate)
                    : getDateInitialValue(getNowByClient(timeZoneId.value));
            });
        }
        watch(() => props.tabname, (tabname) => {
            state.tabname = tabname;
        });
        watch(() => state.tabname, () => {
            state.showChart = false;
            getReportsByInterval(state.tabname);
        });
        watch(() => state.date, () => {
            if (!state.date)
                return;
            getReportsByInterval(state.tabname);
        }, {
            deep: true,
        });
        watch(() => intlFormatLocale.value, () => {
            rerenderDataGrid();
        });
        getServerTime();
        return {
            state,
            options,
            energyBalance: computed(() => getEnergyBalance(state.energyBalance, intlFormatLocale.value, t)),
        };
    },
});
